import { REJECT, array, nullable, num, object, str, type Received } from 'validation';

import { getRecord } from 'api/shared/getRecord';

/**
 * A single queue as returned by nap-backend `GET /queues/:queue_id`
 * (routes/queues.js → getQueues, joined to zones / scheduling_windows). This is
 * the edit-relevant projection re-derived from the backend source, NOT the old
 * `QueueRow` interface, which carried fields the endpoint never returns
 * (`worker_name`, `connection_name`, `created_at`). The endpoint also returns
 * `queue_limit_allocation`, `updated_at`, `zone_id`, `scheduling_window_name`
 * and `scheduling_window_timezone`, which the edit form does not consume, so
 * they are dropped by the object parser. Deeply readonly — validated wire data.
 */
export interface FetchedQueue {
  readonly queue_id: number;
  readonly queue_name: string | null;
  readonly queue_description: string | null;
  readonly queue_status: string | null;
  readonly zone_name: string | null;
  readonly queue_limit: number;
  // Arrays are mutable-outer: the edit form assigns them straight into its
  // (mutable) form state, and `readonly T[]` is not assignable to `T[]`.
  // `queues_workers.priority` / `queues_connections.priority` are nullable columns (DEFAULT 100, so unset only by an
  // explicit null), reaching the wire through `jsonb_build_object`, which carries a SQL null through as a JSON null.
  readonly workers: { readonly worker_id: number; readonly worker_name: string; readonly priority: number | null }[];
  readonly connections: { readonly connection_id: number; readonly connection_name: string; readonly priority: number | null }[];
  readonly scheduling_window_id: number | null;
  readonly queue_allocation_statuses: string[];
  readonly folder_id: number | null;
}

const queueSchema = object<FetchedQueue>({
  queue_id: num(REJECT),
  queue_name: nullable(str()),
  queue_description: nullable(str()),
  queue_status: nullable(str()),
  zone_name: nullable(str()),
  queue_limit: num(),
  workers: array(
    object<{ worker_id: number; worker_name: string; priority: number | null }>({ worker_id: num(), worker_name: str(), priority: nullable(num()) })
  ),
  connections: array(
    object<{ connection_id: number; connection_name: string; priority: number | null }>({
      connection_id: num(),
      connection_name: str(),
      priority: nullable(num()),
    })
  ),
  scheduling_window_id: nullable(num()),
  queue_allocation_statuses: array(str()),
  folder_id: nullable(num()),
});

/**
 * GET `/queues/:id` — one record for the edit dialog, so it works
 * when launched from the Workspace (which never populates the list slice) and
 * never opens on a loosely-cast store row.
 *
 * Boundary-validated. See `getRecord` for what each outcome means; the short
 * version is that `null` means the server said there is no such record, and
 * the failure arm means it said something we could not read. The caller must
 * tell those apart, because refreshing a list helps with only one of them.
 */
export const getQueue = (id: number): Promise<Received<FetchedQueue | null>> => getRecord(`/queues/${id}`, { schema: queueSchema, source: 'getQueue' });
